import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { User } from '@timetable/types';

export type UserRole = 'super_admin' | 'admin' | 'standard' | 'teacher';

// Session type for authentication
export interface Session {
  access_token: string;
  refresh_token: string;
  expires_at: number;
}

interface AuthState {
  user: User | null;
  session: Session | null;
  schoolContext: string | null;
  role: UserRole | null;
  isAuthenticated: boolean;
}

interface AuthActions {
  setAuth: (user: User | null, session: Session | null, role: UserRole | null, schoolContext: string | null) => void;
  clearAuth: () => void;
  setSchoolContext: (schoolId: string) => void;
  clearSchoolContext: () => void;
}

type AuthStore = AuthState & AuthActions;

const initialState: AuthState = {
  user: null,
  session: null,
  schoolContext: null,
  role: null,
  isAuthenticated: false,
};

export const useAuthStore = create<AuthStore>()(
  persist(
    (set) => ({
      ...initialState,
      setAuth: (user, session, role, schoolContext) =>
        set({
          user,
          session,
          role,
          schoolContext,
          isAuthenticated: !!user && !!session,
        }),
      clearAuth: () => set(initialState),
      setSchoolContext: (schoolId) => set({ schoolContext: schoolId }),
      clearSchoolContext: () => set({ schoolContext: null }),
    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => ({
        user: state.user,
        session: state.session,
        role: state.role,
        schoolContext: state.schoolContext,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
